import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "../../helpers/constants";
import { getCookie } from "../../helpers/cookies";
import { paymentHistoryParser } from "./commonParser";

const getBackupFilename = (contentDisposition, fallbackName = `backup-${Date.now()}.zip`) => {
    if (!contentDisposition) {
        return fallbackName;
    }

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
        return decodeURIComponent(utfMatch[1]).replace(/['"]/g, "");
    }

    const plainMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
    if (plainMatch?.[1]) {
        return plainMatch[1];
    }

    return fallbackName;
};

const commonApis = createApi({
    reducerPath: "commonApis",
    baseQuery: fetchBaseQuery({
        baseUrl: baseUrl,
        prepareHeaders: (headers, { getState }) => {
            const token = getCookie("asmc_token");
            if (token) {
                headers.set("Authorization", `BEARER ${token}`);
            }
            return headers;
        },
    }),
    keepUnusedDataFor: 0,
    tagTypes: ["PaymentHistory", "DbBackup", "HomePageCms", "AboutPageCms", "SettingsDefaultCms"],
    endpoints: (build) => ({
        getDashboardCount: build.query({
            query: (params) => ({
                url: "/common/dashboard-count",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => response.result,
        }),
        handleImageUpload: build.mutation({
            query: (payload) => ({
                url: "/common/upload-single-image",
                method: "POST",
                body: payload,
                formData: true,
            }),
            transformResponse: (response) => response.result,
        }),
        handleMultipleImageUpload: build.mutation({
            query: (payload) => ({
                url: "/common/upload-multiple-image",
                method: "POST",
                body: payload,
                formData: true,
            }),
            transformResponse: (response) => response.result,
        }),
        getPaymentHistoryList: build.query({
            query: (params) => ({
                url: "/payment/payment-list",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => paymentHistoryParser(response),
            providesTags: ["PaymentHistory"],
            keepUnusedDataFor: 5,
        }),
        getContactLeadsList: build.query({
            query: (params) => ({
                url: "/common/contact-us",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => response.result,
        }),

        // database backup and fetch
        getDatabaseBackupList: build.query({
            query: (params) => ({
                url: "/common/list_db",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => response.result,
            providesTags: ["DbBackup"],
        }),
        generateNewDatabaseBackup: build.mutation({
            query: () => ({
                url: "/common/export_db",
                method: "get",
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: (response, meta) => ({
                blob: response,
                filename: getBackupFilename(meta?.response?.headers?.get("content-disposition")),
            }),
            invalidatesTags: ["DbBackup"],
        }),
        downloadDatabaseBackup: build.mutation({
            query: (id) => ({
                url: `/common/download_db/${id}`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: (response, meta, id) => ({
                blob: response,
                filename: getBackupFilename(meta?.response?.headers?.get("content-disposition"), `backup-${id}.zip`),
            }),
        }),
        deleteDatabaseBackup: build.mutation({
            query: (params) => ({
                url: "/common/export_db",
                method: "delete",
                params: params,
            }),
            transformResponse: (response) => response.result,
            invalidatesTags: ["DbBackup"],
        }),
        cleanupOldBackups: build.mutation({
            query: () => ({
                url: "/common/cleanup_db_backups",
                method: "POST",
            }),
            transformResponse: (response) => response.result,
            invalidatesTags: ["DbBackup"],
        }),

        // home page cms
        getHomePageCms: build.query({
            query: (params) => ({
                url: "/common/home-page-cms",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => response.result,
            providesTags: ["HomePageCms"],
        }),
        updateHomePageCms: build.mutation({
            query: (payload) => ({
                url: "/common/home-page-cms",
                method: "PUT",
                body: payload,
            }),
            transformResponse: (response) => response.result,
            invalidatesTags: ["HomePageCms"],
        }),

        // about page cms
        getAboutPageCms: build.query({
            query: (params) => ({
                url: "/common/about-page-cms",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => response.result,
            providesTags: ["AboutPageCms"],
        }),
        updateAboutPageCms: build.mutation({
            query: (payload) => ({
                url: "/common/about-page-cms",
                method: "PUT",
                body: payload,
            }),
            transformResponse: (response) => response.result,
            invalidatesTags: ["AboutPageCms"],
        }),

        // settings default page cms
        getSettingsDefaultCms: build.query({
            query: (params) => ({
                url: "/common/settings-default",
                method: "GET",
                params: params,
            }),
            transformResponse: (response) => response.result,
            providesTags: ["SettingsDefaultCms"],
        }),
        updateSettingsDefaultCms: build.mutation({
            query: (payload) => ({
                url: "/common/settings-default",
                method: "PUT",
                body: payload,
            }),
            transformResponse: (response) => response.result,
            invalidatesTags: ["SettingsDefaultCms"],
        }),
    }),
});

export const {
    useGetDashboardCountQuery,
    useHandleImageUploadMutation,
    useHandleMultipleImageUploadMutation,
    useGetPaymentHistoryListQuery,
    useGetContactLeadsListQuery,

    // database backup and fetch
    useGetDatabaseBackupListQuery,
    useGenerateNewDatabaseBackupMutation,
    useDownloadDatabaseBackupMutation,
    useDeleteDatabaseBackupMutation,
    useCleanupOldBackupsMutation,

    // home page cms
    useGetHomePageCmsQuery,
    useUpdateHomePageCmsMutation,
    useGetAboutPageCmsQuery,
    useUpdateAboutPageCmsMutation,

    // settings default page cms
    useGetSettingsDefaultCmsQuery,
    useUpdateSettingsDefaultCmsMutation,
} = commonApis;
export default commonApis;
